/**
 * A document whose fields weren't read, told the way the engine knows it (October 5, 2026).
 *
 * The engine marks a reading that failed because of the field reader itself, not the document
 * (`notReadCause`, FieldReaderProblem.cs): it isn't installed, or it fails even on its own test
 * read. Reading the document again can't fix that, so the screen never promises it: the card says
 * plainly what is wrong, with the exact error behind Technical details, and the engine reads the
 * document again only once the reader reads. Any other failure is a one-off, and keeps
 * "Reading this document again automatically" (the engine does, at 1, 5, 15, then every 60 minutes).
 */

/** The engine's DocumentResult.NotReadCause values. */
export type ReaderProblem = 'field-reader-missing' | 'field-reader-broken';

export const isReaderProblem = (cause: string | null | undefined): cause is ReaderProblem =>
  cause === 'field-reader-missing' || cause === 'field-reader-broken';

/** The card's words: what is wrong, then what happens next. The exact error goes in Technical details. */
export function readerProblemWords(cause: ReaderProblem): { title: string; line: string } {
  return cause === 'field-reader-missing'
    ? {
        title: "Can't read fields: the field reader isn't installed",
        line: 'Paige reads this document once it is installed. Your pages and saved corrections are kept.',
      }
    : {
        title: "Can't read fields: the field reader isn't working",
        line: 'Paige reads this document once it works again. Your pages and saved corrections are kept.',
      };
}

/** The tour's reading window, while its documents wait for the field reader: the same plain words, no promise. */
export const readerProblemStep = (cause: ReaderProblem): string => readerProblemWords(cause).title;
